import { useCallback, useEffect, useState } from 'react';
import { ensureINSToken, handleFailedRefresh } from '../../services/interactiveServiceFetch';
import {
    confirmPendingLink,
    fetchPendingLink,
    fetchSlackAgentStatus,
    startAccountLink,
    startWorkspaceInstall,
    unlinkSlackAccount,
} from '../service';

const useSlackAgentConnection = () => {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        try {
            setStatus(await fetchSlackAgentStatus());
        } catch {
            setStatus(null);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        // bfcache restore after an OAuth hand-off leaves the page stuck in `busy`.
        const clearBusyOnRestore = event => {
            if (event.persisted) setBusy(false);
        };
        window.addEventListener('pageshow', clearBusyOnRestore);
        return () => window.removeEventListener('pageshow', clearBusyOnRestore);
    }, []);

    const beginAuthorize = useCallback(async start => {
        setBusy(true);
        try {
            // Refreshed right before the hand-off: the callback re-authenticates on the way back.
            const refreshResult = await ensureINSToken();
            if (!refreshResult.ok) {
                handleFailedRefresh(refreshResult);
                throw new Error('interactive service session could not be refreshed');
            }
            const { authorize_url: authorizeUrl } = await start();
            window.location.href = authorizeUrl;
        } catch (error) {
            setBusy(false);
            throw error;
        }
    }, []);

    const connectWorkspace = useCallback(() => beginAuthorize(startWorkspaceInstall), [beginAuthorize]);

    const linkAccount = useCallback(() => beginAuthorize(startAccountLink), [beginAuthorize]);

    const unlink = useCallback(async () => {
        setBusy(true);
        try {
            await unlinkSlackAccount(status.workspace.team_id, status.linked_slack_user_id);
            await refresh();
        } finally {
            setBusy(false);
        }
    }, [status, refresh]);

    const openInvite = useCallback(token => fetchPendingLink(token), []);

    const confirmInvite = useCallback(
        async token => {
            setBusy(true);
            try {
                // Captured before the refresh: the caller picks its toast from what the bind reported.
                const linked = await confirmPendingLink(token);
                await refresh();
                return linked;
            } finally {
                setBusy(false);
            }
        },
        [refresh]
    );

    return { status, busy, refresh, connectWorkspace, linkAccount, unlink, openInvite, confirmInvite };
};

export default useSlackAgentConnection;
